import React, { useEffect, useMemo, useState, useCallback } from "react";
import Chart from "react-apexcharts";
import { getProspectHeatmap } from "interfaces/prospect";

type SeriesItem = { name: string; data: { x: number | string; y: number }[] };

// Helpers to compute ISO week dates for labels/tooltips
const isoWeekToDate = (year: number, week: number, isoDow: number): Date => {
    // ISO: week 1 is the week with Jan 4th; isoDow: 1..7 (Mon..Sun)
    const jan4 = new Date(Date.UTC(year, 0, 4));
    const jan4Dow = jan4.getUTCDay() || 7; // 1..7
    const week1Mon = new Date(jan4);
    week1Mon.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
    const d = new Date(week1Mon);
    d.setUTCDate(week1Mon.getUTCDate() + (week - 1) * 7 + (isoDow - 1));
    return d;
};

const monthShort = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ProspectHeatmap: React.FC = () => {
    const [series, setSeries] = useState<SeriesItem[]>([]);
    const [isDemo, setIsDemo] = useState(true);
    const [loading, setLoading] = useState(false);
    const [yearToShow] = useState<number>(new Date().getFullYear());
    const [weeksNumbers, setWeeksNumbers] = useState<number[]>([]); // 0-indexed x corresponds to weeksNumbers[x]
    const [monthCategories, setMonthCategories] = useState<string[]>([]); // x-axis labels like GitHub (sparse month names)

    const loadReal = useCallback(async () => {
        setLoading(true);
        try {
            const project = localStorage.getItem("project-id");
            const rows = await getProspectHeatmap({ project: project || undefined });

            if (!rows || rows.length === 0) {
                setSeries([]);
                setIsDemo(true);
                return;
            }

            // rows: [{ year, week, dow, sum }]
            // Build weeks for year: 1..53 (show all weeks)
            const weeks: number[] = [];
            for (let w = 1; w <= 53; w++) weeks.push(w);
            setWeeksNumbers(weeks);

            // Precompute month categories for each week column (label only on month changes)
            const months: string[] = [];
            let prevMonth = -1;
            for (let i = 0; i < weeks.length; i++) {
                const w = weeks[i];
                const monday = isoWeekToDate(yearToShow, w, 1);
                const m = monday.getUTCMonth();
                if (m !== prevMonth) {
                    months.push(monthShort[m]);
                    prevMonth = m;
                } else {
                    months.push("");
                }
            }
            setMonthCategories(months);

            // Map rows by dow -> week -> sum for the selected year
            const dowMap: Record<number, Record<string, number>> = {};
            rows.forEach((r: any) => {
                const ry = Number(r.year);
                const rw = Number(r.week);
                const rd = Number(r.dow);
                if (ry !== yearToShow) return; // only include selected year
                const key = String(rw);
                dowMap[rd] = dowMap[rd] || {};
                dowMap[rd][key] = (dowMap[rd][key] || 0) + (r.sum || 0);
            });

            // Build series: Monday-first (Mon..Sun)
            const order: Array<{ label: string; dow: number }> = [
                { label: "Mon", dow: 1 },
                { label: "Tue", dow: 2 },
                { label: "Wed", dow: 3 },
                { label: "Thu", dow: 4 },
                { label: "Fri", dow: 5 },
                { label: "Sat", dow: 6 },
                { label: "Sun", dow: 7 },
            ];
            // Use numeric x = column index, and categories will provide month labels
            const newSeries = order.map(({ label, dow }) => {
                const data = weeks.map((w, idx) => {
                    const raw = Number(dowMap[dow]?.[String(w)] ?? 0);
                    // Distinguish weekend zero vs weekday zero via sentinels
                    // -102 => weekend-zero (Sat/Sun), -101 => weekday-zero
                    const y = raw === 0 ? (dow >= 6 ? -102 : -101) : raw;
                    return { x: idx, y };
                });
                return { name: label, data };
            });

            setSeries(newSeries);
            setIsDemo(newSeries.every((g) => g.data.every((d) => d.y === 0)));
        } finally {
            setLoading(false);
        }
    }, [yearToShow]);

    const generateDemo = useCallback((seedYear = yearToShow) => {
        setLoading(true);
        try {
            const weeks: number[] = [];
            for (let w = 1; w <= 53; w++) weeks.push(w);
            setWeeksNumbers(weeks);

            // month categories like real data
            const months: string[] = [];
            let prevMonth = -1;
            for (let i = 0; i < weeks.length; i++) {
                const w = weeks[i];
                const monday = isoWeekToDate(seedYear, w, 1);
                const m = monday.getUTCMonth();
                if (m !== prevMonth) {
                    months.push(monthShort[m]);
                    prevMonth = m;
                } else {
                    months.push("");
                }
            }
            setMonthCategories(months);

            const order: Array<{ label: string; dow: number }> = [
                { label: "Mon", dow: 1 },
                { label: "Tue", dow: 2 },
                { label: "Wed", dow: 3 },
                { label: "Thu", dow: 4 },
                { label: "Fri", dow: 5 },
                { label: "Sat", dow: 6 },
                { label: "Sun", dow: 7 },
            ];

            const newSeries = order.map(({ label, dow }) => {
                const data = weeks.map((w, idx) => {
                    // create sparser activity similar to GitHub commits
                    const active = Math.random() > 0.75; // ~25% chance of any activity
                    const raw = active ? Math.ceil(Math.random() * 8) : 0;
                    const y = raw === 0 ? (dow >= 6 ? -102 : -101) : raw;
                    return { x: idx, y };
                });
                return { name: label, data };
            });

            setSeries(newSeries);
            setIsDemo(true);
        } finally {
            setLoading(false);
        }
    }, [yearToShow]);

    useEffect(() => {
        // load real data on mount
        loadReal();
    }, [loadReal]);

    const tooltipHtml = useMemo(() => {
        // Precompute a function to render tooltip similar to GitHub
    const dowOrder: number[] = [1, 2, 3, 4, 5, 6, 7]; // seriesIndex -> isoDow (Mon..Sun)
        return (w: any) => {
            const { series, seriesIndex, dataPointIndex } = w;
            if (
                seriesIndex == null ||
                dataPointIndex == null ||
                !weeksNumbers.length ||
                !series ||
                !series[seriesIndex]
            ) {
                return "";
            }
            const val = Number(series[seriesIndex][dataPointIndex] ?? 0);
            const week = weeksNumbers[dataPointIndex] ?? 1;
            const isoDow = dowOrder[seriesIndex] ?? 7;
            const date = isoWeekToDate(yearToShow, week, isoDow);
            const yyyy = date.getUTCFullYear();
            const mm = monthShort[date.getUTCMonth()];
            const dd = date.getUTCDate();
            const dayOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getUTCDay()];
            const dateStr = `${dayOfWeek}, ${mm} ${dd}, ${yyyy}`;
            const displayVal = val <= 0.5 ? 0 : val;
            const label = displayVal === 0 ? "No prospecting" : `${displayVal} prospect${displayVal > 1 ? "s" : ""}`;
            return `
                <div style="padding:6px 8px; font-size:12px;">
                    <div><strong>${label}</strong></div>
                    <div style="color:#6b7280; margin-top:2px;">on ${dateStr}</div>
                </div>
            `;
        };
    }, [weeksNumbers, yearToShow]);

    const options = {
        chart: {
            type: "heatmap",
            toolbar: { show: false },
            animations: { enabled: false },
        },
        plotOptions: {
            heatmap: {
                // flat design: no shading gradient
                shadeIntensity: 0,
                enableShades: false,
                radius: 2,
                useFillColorAsStroke: false,
                // white stroke gives the GitHub-like separation between squares
                stroke: {
                    width: 2,
                    color: "#ffffff",
                },
                // enforce discrete ranges (mode: 'range') so 0 maps to grey reliably
        colorScale: {
                    mode: "range",
                    ranges: [
                    // special sentinels for zero-count cells (weekday vs weekend)
                    { from: -102, to: -102, color: "#E9EFF5", name: "weekend-0" }, // weekend zero (Sat/Sun)
                    { from: -101, to: -101, color: "#e6ecf2", name: "0" }, // weekday zero
                        { from: 1, to: 1, color: "#bfdbfe", name: "1" },
                        { from: 2, to: 3, color: "#93c5fd", name: "2-3" },
                        { from: 4, to: 7, color: "#60a5fa", name: "4-7" },
                        { from: 8, to: 9999, color: "#3b82f6", name: ">=8" },
                    ],
                },
            },
        },
    fill: { type: "solid", opacity: 1 },
        dataLabels: { enabled: false },
        tooltip: {
            enabled: true,
            theme: "light",
            custom: tooltipHtml,
        },
        xaxis: {
            categories: monthCategories,
            tickPlacement: "between",
            labels: {
                show: true,
                rotate: 0,
                style: { colors: "#6b7280", fontSize: "10px" },
                trim: true,
            },
            axisTicks: { show: false },
            axisBorder: { show: false },
            tooltip: { enabled: false },
        },
        yaxis: { show: false },
        grid: { padding: { left: 6, right: 0, top: 0, bottom: 0 } },
        legend: { show: false },
    } as any;

    return (
        <div className="relative px-3 bg-white rounded-sm shadow-sm mx-auto max-w-full border overflow-x-auto">
            <div className="flex items-centerjustify-end mb-2">
                <button
                    className={`text-xs px-2 py-1`}
                    onClick={() => {
                        if (!isDemo) {
                            // enable demo
                            generateDemo();
                            setIsDemo(true);
                        } else {
                            // switch back to real data
                            loadReal();
                            setIsDemo(false);
                        }
                    }}
                    disabled={loading}
                >
                    {isDemo ? "" : ""}
                </button>
            </div>

            {series.length === 0 ? (
                <div className="flex items-center justify-center h-40 text-gray-500">No prospecting data available</div>
            ) : (
                <>
                    <div className="grid grid-cols-[auto_1fr] gap-2 items-start">
                        <div className="flex flex-col items-end mt-7">
                            {["S", "S", "F", "T", "W", "T", "M"].map((label, i) => (
                                <div key={i} className="text-[10px] leading-4 align-center mx-auto text-center h-[13px] text-gray-500 font-bold w-2">
                                    {label}
                                </div>
                            ))}
                        </div>
                        <div className="overflow-auto">
                            <Chart options={options} series={series as any} type="heatmap" height={150} />
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default ProspectHeatmap;
